/**
 * useAttentionItems.ts — список «Требует внимания» проекта или организации
 * (WEB-3780, WEB-3784) на ручках сервера WEB-3778 / WEB-3779.
 *
 * Хук только загружает: порядок, маски денег и прав, отбор по проекту и
 * организации решает сервер — хук не сортирует, не фильтрует и не
 * досчитывает, `data` — ответ ручки тем же объектом. `unchecked` («сервер не
 * смог проверить», а не «всё хорошо») отдаётся как есть.
 *
 * Устаревший ответ (на запрос для прежнего scope или до последнего refresh)
 * отбрасывается: у каждого запуска эффекта свой флаг жизни, его гасит
 * очистка при смене scope, refresh и размонтировании. Данные прежнего scope
 * не показываются под новым уже на первом кадре — состояние помнит, к
 * какому scope оно относится. Отказ ручки кладётся в `error`, прежний
 * успешный ответ того же scope остаётся. Опроса по таймеру нет.
 *
 * Состояние локальное, без Redux: список нужен двум страницам, общего кэша
 * не требуется. Пока никем не смонтирован — блок WEB-3781/3782.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  attentionApi,
  type OrganizationAttention,
  type ProjectAttention,
} from '@/api/attention';

export type AttentionScope =
  | { kind: 'project'; projectId: string }
  | { kind: 'org'; orgId: string }
  | null;

export type AttentionData = ProjectAttention | OrganizationAttention;

/** Ошибка общего обработчика `api/client.ts`: со `status` и `code`, если сервер их дал. */
export type AttentionError = Error & { status?: number; code?: string };

export interface AttentionItemsState {
  data: AttentionData | null;
  loading: boolean;
  error: AttentionError | null;
  refresh: () => void;
}

interface Loaded {
  /** К какому scope относится состояние — `kind:id`. */
  key: string | null;
  data: AttentionData | null;
  loading: boolean;
  error: AttentionError | null;
}

const EMPTY: Loaded = { key: null, data: null, loading: false, error: null };

export function useAttentionItems(scope: AttentionScope): AttentionItemsState {
  // Сравнение по виду и идентификатору: тот же scope новым объектом
  // повторного запроса не шлёт.
  const kind = scope?.kind ?? null;
  const id =
    scope === null
      ? null
      : scope.kind === 'project'
        ? scope.projectId
        : scope.orgId;
  const key = kind === null || id === null ? null : `${kind}:${id}`;

  const [state, setState] = useState<Loaded>(EMPTY);
  const [round, setRound] = useState(0);

  useEffect(() => {
    if (kind === null || id === null) {
      setState(EMPTY);
      return undefined;
    }
    const myKey = `${kind}:${id}`;
    let alive = true;
    setState((prev) =>
      prev.key === myKey
        ? { ...prev, loading: true }
        : { key: myKey, data: null, loading: true, error: null },
    );
    const request =
      kind === 'project'
        ? attentionApi.forProject(id)
        : attentionApi.forOrganization(id);
    request.then(
      (data) => {
        if (!alive) return;
        setState({ key: myKey, data, loading: false, error: null });
      },
      (err: unknown) => {
        if (!alive) return;
        const error = (
          err instanceof Error ? err : new Error(String(err))
        ) as AttentionError;
        setState((prev) => ({
          key: myKey,
          data: prev.key === myKey ? prev.data : null,
          loading: false,
          error,
        }));
      },
    );
    return () => {
      alive = false;
    };
  }, [kind, id, round]);

  const refresh = useCallback(() => setRound((n) => n + 1), []);

  if (key === null) {
    return { data: null, loading: false, error: null, refresh };
  }
  if (state.key !== key) {
    // Первый кадр после смены scope, эффект ещё не отработал.
    return { data: null, loading: true, error: null, refresh };
  }
  return {
    data: state.data,
    loading: state.loading,
    error: state.error,
    refresh,
  };
}
